import React, { useState, useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, signOut, onIdTokenChanged, createUserWithEmailAndPassword } from 'firebase/auth';

import { auth } from "./Firebase"
// import { ref, set, update } from "firebase/database";
// import { database } from "../Components/FirebaseConfig";
import { database } from "./Firebase";

const AuthContext = React.createContext({
  token: '',
  userId: '',
  userName: '',
  displayName: '',
  email: '',
  avatarRef: '',
  userAccess: '',
  isLoggedIn: false,
  login: (email, password) => {},
  logout: () => {}
});

export const AuthContextProvider = (props) => {

  //firebase db
  const db = database;

  const initialToken = localStorage.getItem('delivaToken');
  const initialUserId = localStorage.getItem('userId');
  const initialUserName = localStorage.getItem('userName');
  const initialDisplayName = localStorage.getItem('displayName');
  const initialEmail = localStorage.getItem('email');
  const initialAvatarRef = localStorage.getItem('avatarRef');
  const initialUserAccess = localStorage.getItem('userAccess');

  const [token, setToken] = useState(initialToken);
  const [userId, setUserId] = useState(initialUserId);
  const [userName, setUserName] = useState(initialUserName);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [email, setEmail] = useState(initialEmail);
  const [avatarRef, setAvatarRef] = useState(initialAvatarRef);
  const [userAccess, setUserAccess] = useState(initialUserAccess);
  const [error, setError] = useState("no error");
  const [isTokenRefreshing, setIsTokenRefreshing] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState(false);

  const userIsLoggedIn = !!token;  

  // Token refresh handler
  const refreshToken = useCallback(async (user) => {
    if (!user) return;
    
    try {
      setIsTokenRefreshing(true);
      const newToken = await user.getIdToken(true); // Force refresh
      setToken(newToken);
      localStorage.setItem('delivaToken', newToken);
    } catch (error) {
      // Handle token refresh failure - might need to logout user
      logoutHandler();
    } finally {
      setIsTokenRefreshing(false);
    }
  }, []);

  // Helper to clear all user state and localStorage
  const clearUserState = () => {
    setToken(null);
    setUserId(null);
    setUserName(null);
    setDisplayName(null);
    setEmail(null);
    setAvatarRef(null);
    setUserAccess(null);
    localStorage.removeItem('delivaToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('displayName');
    localStorage.removeItem('email');
    localStorage.removeItem('avatarRef');
    localStorage.removeItem('userAccess');
  };

  // Enhanced token change listener with refresh logic
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      // Don't process auth changes during login process
      if (loginInProgress) {
        return;
      }
      
      if (user) {
        try {
          // Get fresh token
          const freshToken = await user.getIdToken();
          setToken(freshToken);
          setUserId(user.uid);
          localStorage.setItem('delivaToken', freshToken);
          localStorage.setItem('userId', user.uid);
          
          // Check if token is about to expire (within 5 minutes)
          const tokenResult = await user.getIdTokenResult();
          const expirationTime = tokenResult.expirationTime;
          const currentTime = new Date().getTime() / 1000;
          
          // Validate expiration time before using it
          if (expirationTime && !isNaN(expirationTime) && expirationTime > 0) {
            const timeUntilExpiry = expirationTime - currentTime;
            
            // If token expires in less than 5 minutes, refresh it
            if (timeUntilExpiry < 300) { // 5 minutes = 300 seconds
              await refreshToken(user);
            }
          }
        } catch (error) {
          setError(error.message);
        }
      } else {
        // User signed out
        clearUserState();
      }
    });

    return () => unsubscribe();
  }, [refreshToken, loginInProgress]);

  // Set up periodic token refresh (every 50 minutes)
  useEffect(() => {
    if (!userIsLoggedIn) return;

    const refreshInterval = setInterval(async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await refreshToken(currentUser);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [userIsLoggedIn, refreshToken]);

  const loginHandler = async (email, password) => {
    // Validate and sanitize parameters
    const sanitizedEmail = email.trim();
    
    setLoginInProgress(true);
    
    try {
      const data = await signInWithEmailAndPassword(auth, sanitizedEmail, password);
      
      const userInfoResponse = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/administrators/${data.user.uid}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${data.user.accessToken}` 
        }
      });
      
      const userInfoData = await userInfoResponse.json();
      
      if (userInfoResponse.status === 403 || userInfoData.status !== 'success') {
        await signOut(auth);
        clearUserState();
        throw new Error(userInfoData.message || 'Access denied. Only administrators can access this panel.');
      }
      
      const userAccess = userInfoData.data.access;
      if (userAccess === 'user' || (userAccess !== 'admin' && userAccess !== 'superadmin')) {
        await signOut(auth);
        clearUserState();
        throw new Error('Access denied. Only administrators can access this panel.');
      }
      
      setToken(data.user.accessToken);
      setUserId(data.user.uid);
      setUserName(userInfoData.data.name);
      setDisplayName(userInfoData.data.username);
      setEmail(sanitizedEmail || userInfoData.data.email);
      setAvatarRef(userInfoData.data.avatarRef);
      setUserAccess(userAccess);
      setError("no error");

      localStorage.setItem('delivaToken', data.user.accessToken);
      localStorage.setItem('userId', data.user.uid);
      localStorage.setItem('userName', userInfoData.data.name);
      localStorage.setItem('displayName', userInfoData.data.username);
      localStorage.setItem('email', sanitizedEmail || userInfoData.data.email);
      localStorage.setItem('avatarRef', userInfoData.data.avatarRef);
      localStorage.setItem('userAccess', userAccess);

      setLoginInProgress(false);
      return { success: true };

    } catch (error) {
      await signOut(auth);
      clearUserState();
      setError(error.message);
      setLoginInProgress(false);
      throw error;
    }
  };

  const logoutHandler = async () => {
    try {
      await signOut(auth);
      clearUserState();
    } catch (error) {
      setError(error.message);
    }
  };

  const clearError = () => {
    setError("no error");
  };

  const createUserHandler = async (email, password, name)=>{

    createUserWithEmailAndPassword(auth, email.trim(), password, name)
      .then((data) => {

        const uid = data.user.uid
        const tempToken = data.user.accessToken

        var userPayload = {
          firstName:"test", 
          lastName:"test", 
          email: email, 
          password: password
        };
        console.log(userPayload)

        fetch(process.env.REACT_APP_API_URI + `/v1/register`, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tempToken}` },
          body: JSON.stringify(userPayload)
        }).then(response => {
          return response.json();
        }).then(data => {
          console.log(data);
          if (data.status == "success") {

            // set(ref(db, `users/${uid}`), { "email": email })
            //   .then(() => {
            //     console.log('user added')
            //     getUserData(uid, tempToken)
            //   })
            //   .catch((error) => {
            //     console.log(error)
            //   });

          } else {
            console.log('something went wrong while adding user')
          }
        });
      })
      .catch((e) => {
        console.log(e);
        console.log(e.code);
        if (e.code === 'weak-password') {
          console.log("The password provided is too weak.")
        } else if (e.code === 'email-already-in-use') {
          console.log("The account already exists for that email.")
        } else if (e.code === 'too-many-requests') {
          console.log("Sorry, failed too many signup attempts. Please try again later")
        }
      });
  }

  const getUserData = (uid, tempToken) => {

    console.log(uid, tempToken)

    // fetch(process.env.REACT_APP_API_URI + `/v1/user/${uid}`, {
    //   method: 'GET',
    //   mode: 'cors',
    //   headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tempToken}` },
    // }).then(response => {
    //   return response.json();
    // }).then(data => {
    //   console.log(data);
    //   if(data.status == "success") {
    //     setToken(tempToken);
    //     setUserId(data.data.id);
    //     setUserName(data.data.name);
    //     setDisplayName(data.data.username);
    //     setEmail(data.data.email);
    //     setAvatarRef(data.data.avatarRef);
  
    //     localStorage.setItem('token', tempToken);
    //     localStorage.setItem('userId', data.data.id);
    //     localStorage.setItem('userName', data.data.username);
    //     localStorage.setItem('displayName', data.data.name);
    //     localStorage.setItem('email', data.data.email);
    //     localStorage.setItem('avatarRef', data.data.avatarRef);
  
    //   } else {
    //     console.log('something went wrong while getting user')
    //   }
    // });

  }

  const contextValue = {
    token: token,
    userId: userId,
    userName: userName,
    displayName: displayName,
    email: email,
    avatarRef: avatarRef,
    userAccess: userAccess,
    isLoggedIn: userIsLoggedIn,
    error: error,
    isTokenRefreshing: isTokenRefreshing,
    loginInProgress: loginInProgress,
    login: loginHandler,
    logout: logoutHandler,
    createUser: createUserHandler,
    refreshToken: refreshToken,
    clearError: clearError
  };
  
  return <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>;
}

export default AuthContext;